import { Box, Grid, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { HomeWorkOutlined } from '@material-ui/icons';
import {
  PPP_3_2_1,
  PPP_4_3_2_1,
  PPP_5_4_3_2_1,
  PPP_YIELD_MAINTENANCE,
} from '@roc/client-portal-shared/utils';
import { FormStore } from '@roc/feature-app-core';
import { CurrencyField, FieldLabel, PercentageField, Slider } from '@roc/ui';
import { observer } from 'mobx-react';
import {
  createCurrencyField,
  createNumberFormatField,
  createPercentageField,
  createSelectField,
} from '../temp/componentBuiderTemp';
import { RoundedBackgroundIcon } from './roundedBackgroundIcon';
import { OneToolCard, OneToolCardBorder } from '../fixFlip/oneToolCard';
import {
  amortizationOptions,
  FORM_MAX_WIDTH,
  prepaymentPenaltyOptions,
  rateTypeOptions,
} from '../../utils/constants';
import { useEffect, useState } from 'react';

const useStyles = makeStyles(theme => ({
  paper: {
    border: `1px solid lightgray`,
    borderRadius: '16px !important',
    padding: theme.spacing(4),
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    fontSize: '1rem',
  },
}));

export const LoanTermsCard = observer(({ store }: any) => {
  const classes = useStyles();
  const formStore = store.loanEconomicsStore.loanEconomicsFormStore;
  const calculatedFields = store.loanEconomicsStore.calculateFields();
  const loanAmount = formStore.form.fields.loanAmount.value;
  const [loanToValue, setLoanToValue] = useState<string>('');

  useEffect(() => {
    setLoanToValue((calculatedFields.loanToValue ?? 0).toFixed(2));
  }, [loanAmount]);

  return (
    <OneToolCardBorder>
      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        alignItems="center"
      >
        <Grid item>
          <RoundedBackgroundIcon Icon={HomeWorkOutlined} iconColor="#6D6D6D" />
        </Grid>
        <Grid item>
          <Typography variant="h4" style={{ color: '#6D6D6D' }}>
            Loan Terms
          </Typography>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="center" pt={4}>
        <Box maxWidth={FORM_MAX_WIDTH}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <AmortizationType store={formStore} />
            </Grid>
            <Grid item xs={6}>
              <RateType store={formStore} />
            </Grid>
            <Grid item xs={6}>
              <PrepaymentPenalty store={formStore} />
            </Grid>
            <Grid item xs={6}>
              <InterestRate store={formStore} />
            </Grid>
            <Grid item xs={6}>
              <LoanAmount store={formStore} />
            </Grid>
            <Grid item xs={6}>
              <LoanToValue
                store={formStore}
                loanToValue={loanToValue}
                setLoanToValue={ltv => setLoanToValue(ltv)}
                totalPurchasePrice={calculatedFields.totalPurchasePrice}
              />
            </Grid>
            <Grid item xs={6}>
              <BrokerPoints store={formStore} />
            </Grid>
            <Grid item xs={6}>
              <EstimatedMonthlyPayment
                store={formStore}
                value={calculatedFields.monthlyPIPayment}
              />
            </Grid>
            <Grid item xs={6}>
              <EstimatedMonthlyPITIAPayment
                store={formStore}
                value={calculatedFields.monthlyPITIAPayment}
              />
            </Grid>
          </Grid >
        </Box >
      </Box >
    </OneToolCardBorder >
  );
});

export const AmortizationType = observer(({ store }: { store: FormStore }) => {
  return (
    <>
      {createSelectField({
        store,
        testId: 'amortizationType',
        fieldName: 'amortizationType',
        label: 'Amortization Type*',
        options: amortizationOptions,
        standaloneLabel: true,
        addMarkForRequiredFields: false,
        isRequired: true,
      })}
    </>
  );
});

export const RateType = observer(({ store }: { store: FormStore }) => {
  return (
    <>
      {createSelectField({
        store,
        testId: 'rateType',
        fieldName: 'rateType',
        label: 'Rate Type',
        options: rateTypeOptions,
        standaloneLabel: true,
        addMarkForRequiredFields: true,
        isRequired: true,
      })}
    </>
  );
});

export const PrepaymentPenalty = observer(({ store }: { store: FormStore }) => {
  return createSelectField({
    store,
    label: 'Prepayment Penalty',
    testId: 'prePaymentPenalty',
    fieldName: 'prePaymentPenalty',
    options: prepaymentPenaltyOptions,
  });
});

export const InterestRate = observer(({ store }: { store: FormStore }) => {
  return createPercentageField({
    store,
    label: 'Interest Rate',
    testId: 'interestRate',
    fieldName: 'interestRate',
  });
});

export const BrokerPoints = observer(({ store }: { store: FormStore }) => {
  return createPercentageField({
    store,
    label: 'Broker Points',
    testId: 'brokerPoints',
    fieldName: 'brokerPoints',
  });
});

export const LoanAmount = observer(({ store }) => {
  const formFields = store.form.fields;
  const fieldName = 'loanAmount';

  return createCurrencyField({
    store,
    label: 'Loan Amount',
    testId: fieldName,
    fieldName,
    allowNegative: false,
  });
});

export const LoanToValue = observer(
  ({ store, loanToValue, setLoanToValue, totalPurchasePrice }) => {
    return (
      <PercentageField
        standaloneLabel
        label="Loan To Value"
        testId="loanToValue"
        variant="outlined"
        fullWidth
        value={loanToValue}
        onChange={(_, value) => {
          setLoanToValue(value.toString());
        }}
        onBlur={() => {
          try {
            const amount = totalPurchasePrice * (parseFloat(loanToValue) / 100);
            store.onFieldChange('loanAmount', amount);
          } catch (e) {}
        }}
      />
    );
  }
);

export const EstimatedMonthlyPayment = observer(
  ({ store, value }: { store: FormStore; value: number }) => {
    return (
      <CurrencyField
        testId={'estimatedMonthlyPayment'}
        disabled={true}
        variant={'outlined'}
        standaloneLabel={true}
        label={'Estimated Monthly Payment'}
        value={value}
        onChange={() => null}
        fullWidth
      />
    );
  }
);

export const EstimatedMonthlyPITIAPayment = observer(
  ({ store, value }: { store: FormStore; value: number }) => {
    return (
      <CurrencyField
        testId={'estimatedMonthlyPITIAPayment'}
        disabled={true}
        variant={'outlined'}
        standaloneLabel={true}
        label={'Estimated Monthly PITIA Payment'}
        value={value}
        onChange={() => null}
        fullWidth
      />
    );
  }
);
